"use client";
import { captureRegistrationIntent, selectedPlanPath } from "@/lib/registration-intent";

import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  ArrowLeft,
  Eye,
  EyeOff,
  LoaderCircle,
  LockKeyhole,
  Mail,
} from "lucide-react";
import { FormEvent, useEffect, useState } from "react";

import Logo from "@/components/Logo";
import GoogleAuthButton from "@/components/auth/GoogleAuthButton";
import { ApiError } from "@/lib/api";
import {
  authenticateWithGoogle,
  confirmTwoFactorLogin,
  getCurrentUser,
  loginUser,
} from "@/lib/auth";
import type { ApiValidationError } from "@/types/auth";

import { useRegisterTypeModal } from "@/components/auth/RegisterTypeModalProvider";
function getApiErrorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return "Não foi possível entrar. Tente novamente.";
  }

  if (error.status === 401) {
    return "E-mail ou senha inválidos.";
  }

  const data = error.data;

  if (!data || typeof data !== "object") {
    return error.message;
  }

  const validationErrors = data as ApiValidationError;

  for (const value of Object.values(validationErrors)) {
    if (typeof value === "string") {
      return value;
    }

    if (Array.isArray(value) && value.length > 0) {
      return String(value[0]);
    }
  }

  return error.message;
}

export default function LoginForm() {
  const { openRegisterTypeModal } = useRegisterTypeModal();

  const router = useRouter();
  useEffect(() => { captureRegistrationIntent(window.location.search); }, []);

  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [challengeToken, setChallengeToken] = useState<string | null>(null);


  async function redirectAfterLogin() {
    const user = await getCurrentUser();

    if (user.has_company) {
      router.replace(selectedPlanPath());
      return;
    }

    router.replace(
      user.registration_type === "individual"
        ? "/pessoa-fisica/cadastro"
        : "/empresa/cadastro",
    );
  }

  async function handleTwoFactorSubmit(
    event: FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault();

    if (!challengeToken || isSubmitting) {
      return;
    }

    const formData = new FormData(event.currentTarget);
    const code = String(formData.get("code") ?? "").trim();

    setErrorMessage("");
    setIsSubmitting(true);

    try {
      await confirmTwoFactorLogin(challengeToken, code);
      await redirectAfterLogin();
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleGoogleCredential(
    credential: string,
  ) {
    if (isSubmitting) {
      return;
    }

    setErrorMessage("");
    setIsSubmitting(true);

    try {
      const response =
        await authenticateWithGoogle({
          credential,
        });

      if ("two_factor_required" in response) {
        setChallengeToken(response.challenge_token);
        return;
      }

      await redirectAfterLogin();
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.status === 400
      ) {
        setErrorMessage(
          "Esta conta Google ainda não está cadastrada. " +
            "Clique em Cadastre-se e escolha Empresa ou Pessoa Física.",
        );
      } else {
        setErrorMessage(
          getApiErrorMessage(error),
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    setErrorMessage("");

    const formData = new FormData(event.currentTarget);

    const email = String(formData.get("email") ?? "")
      .trim()
      .toLowerCase();

    const password = String(formData.get("password") ?? "");

    setIsSubmitting(true);

    try {
      const response = await loginUser({
        username: email,
        password,
      });

      if ("two_factor_required" in response) {
        setChallengeToken(response.challenge_token);
        return;
      }

      await redirectAfterLogin();
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen w-full bg-slate-50">
      <section className="flex w-full items-center justify-center px-5 py-8 lg:w-1/2 lg:px-12">
        <div className="w-full max-w-[430px]">
          <Link
            href="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition-colors hover:text-blue-600"
          >
            <ArrowLeft size={17} />
            Voltar
          </Link>

          <div className="mt-7">
            <Logo compact />
          </div>

          <div className="mt-8">
            <h1 className="text-3xl font-bold tracking-tight text-slate-950">
              Bem-vindo de volta
            </h1>

            <p className="mt-2 text-base text-slate-500">
              Acesse sua conta para gerenciar sua loja.
            </p>
          </div>

          {!challengeToken && <GoogleAuthButton
            onCredential={handleGoogleCredential}
            disabled={isSubmitting}
            className="mt-7"
          />}

          {!challengeToken && <div className="my-6 flex items-center gap-4">
            <div className="h-px flex-1 bg-slate-200" />

            <span className="text-xs text-slate-400">
              ou continue com e-mail
            </span>

            <div className="h-px flex-1 bg-slate-200" />
          </div>}

          {challengeToken ? (
            <form method="post" onSubmit={handleTwoFactorSubmit} className="mt-7">
              <p className="text-sm text-slate-600">
                Digite o código do aplicativo Authenticator ou um código de recuperação.
              </p>
              <label
                htmlFor="two-factor-code"
                className="mt-5 block text-sm font-semibold text-slate-800"
              >
                Código de verificação
              </label>
              <input
                id="two-factor-code"
                name="code"
                type="text"
                autoComplete="one-time-code"
                autoFocus
                required
                disabled={isSubmitting}
                placeholder="Código de 6 dígitos"
                className="mt-2 h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              />
              {errorMessage && (
                <div role="alert" className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                  {errorMessage}
                </div>
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                className="mt-6 h-12 w-full rounded-2xl bg-blue-600 text-sm font-bold text-white disabled:opacity-70"
              >
                {isSubmitting ? "Verificando..." : "Confirmar código"}
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => {
                  setChallengeToken(null);
                  setErrorMessage("");
                }}
                className="mt-4 w-full text-sm font-semibold text-blue-600"
              >
                Voltar ao login
              </button>
            </form>
          ) : (
          <form method="post" onSubmit={handleSubmit}>
            <div>
              <label
                htmlFor="email"
                className="text-sm font-semibold text-slate-800"
              >
                E-mail
              </label>

              <div className="mt-2 flex h-12 items-center gap-3 rounded-2xl border border-slate-300 bg-white px-4 focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-100">
                <Mail size={18} className="text-slate-400" />

                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="seu@email.com"
                  autoComplete="email"
                  disabled={isSubmitting}
                  required
                  className="h-full min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed"
                />
              </div>
            </div>

            <div className="mt-5">
              <label
                htmlFor="password"
                className="text-sm font-semibold text-slate-800"
              >
                Senha
              </label>

              <div className="mt-2 flex h-12 items-center gap-3 rounded-2xl border border-slate-300 bg-white px-4 focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-100">
                <LockKeyhole size={18} className="text-slate-400" />

                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Digite sua senha"
                  autoComplete="current-password"
                  disabled={isSubmitting}
                  required
                  className="h-full min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed"
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  disabled={isSubmitting}
                  className="text-slate-400 transition-colors hover:text-blue-600 disabled:cursor-not-allowed"
                  aria-label={
                    showPassword ? "Ocultar senha" : "Mostrar senha"
                  }
                >
                  {showPassword ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between gap-4">
              <label className="flex items-center gap-2 text-sm text-slate-500">
                <input
                  type="checkbox"
                  disabled={isSubmitting}
                  className="h-4 w-4 rounded border-slate-300 accent-blue-600 disabled:cursor-not-allowed"
                />
                Lembrar acesso
              </label>

              <Link
                href="/recuperar-senha"
                className="text-sm font-semibold text-blue-600 transition-colors hover:text-blue-700"
              >
                Esqueceu a senha?
              </Link>
            </div>

            {errorMessage && (
              <div
                role="alert"
                className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
              >
                {errorMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-6 inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 text-sm font-bold text-white shadow-[0_12px_30px_rgba(37,99,235,0.25)] transition-all hover:-translate-y-0.5 hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0"
            >
              {isSubmitting ? (
                <>
                  <LoaderCircle size={18} className="animate-spin" />
                  Entrando...
                </>
              ) : (
                "Entrar"
              )}
            </button>
          </form>
          )}

          <p className="mt-6 text-center text-sm text-slate-500">
            Ainda não tem uma conta?{" "}
            <button
              type="button"
              onClick={openRegisterTypeModal}
              className="font-semibold text-blue-600 hover:text-blue-700"
            >
              Cadastre-se
            </button>
          </p>
        </div>
      </section>

      <aside className="relative hidden min-h-screen w-1/2 overflow-hidden bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-600 lg:flex lg:items-center lg:justify-center">
        <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-28 -left-20 h-96 w-96 rounded-full bg-cyan-300/20 blur-3xl" />

        <div className="relative z-10 max-w-lg px-12 text-center text-white">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-3xl bg-white/15 backdrop-blur-sm">
            <LockKeyhole size={36} />
          </div>

          <h2 className="mt-8 text-4xl font-bold tracking-tight">
            Sua loja, sempre ao seu alcance
          </h2>

          <p className="mt-5 text-lg leading-8 text-blue-50">
            Gerencie produtos, acompanhe pedidos e mantenha sua vitrine sempre
            atualizada em um só lugar.
          </p>
        </div>
      </aside>
    </div>
  );
}
